// Petsmate — formulary root
// Combines Part A (50) + Part B (50) = 100 built-in canine/feline conditions,
// plus user-added custom diseases persisted in the browser (localStorage).

import { DISEASES_A } from "./diseases_A";
import { DISEASES_B } from "./diseases_B";

export const SPECIES = [
  { value: "canine", label: "Canine (Dog)" },
  { value: "feline", label: "Feline (Cat)" },
];

const CUSTOM_KEY = "petsmate.customDiseases.v1";
const CUSTOM_DRUGS_KEY = "petsmate.customDrugs.v1";

let BACKEND_DISEASES = null;

export const setBackendDiseases = (diseases) => {
  if (!Array.isArray(diseases) || diseases.length === 0) return;

  BACKEND_DISEASES = diseases.map(normaliseDisease);
};

/**
 * Custom disease store — persisted to localStorage. Consumers should call
 * getAllDiseases() (which combines built-in + custom) any time they need
 * the current list. The DRUG_LIBRARY and species/category filters are
 * derived on demand from the same combined list.
 */
export const readCustomDiseases = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(CUSTOM_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const writeCustomDiseases = (list) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(CUSTOM_KEY, JSON.stringify(list));
};

export const BUILT_IN_DISEASES = [...DISEASES_A, ...DISEASES_B];

/** Full list = built-in + custom (custom overrides built-in with same id). */
export const getAllDiseases = () => {
  const custom = readCustomDiseases();

  const base = BACKEND_DISEASES || BUILT_IN_DISEASES;

  const customIds = new Set(custom.map((d) => d.id));

  const filteredBase = base.filter(
    (d) => !customIds.has(d.id)
  );

  return [...filteredBase, ...custom];
};

export const isCustomDisease = (id) =>
  readCustomDiseases().some((d) => d.id === id);

export const getDiseasesForSpecies = (species) =>
  getAllDiseases()
    .filter((d) => d.species.includes(species))
    .sort((a, b) => a.name.localeCompare(b.name));

export const getDiseaseById = (id) => getAllDiseases().find((d) => d.id === id);

/** Save (create or update) a custom disease. Returns the persisted disease. */
export const saveCustomDisease = (disease) => {
  const list = readCustomDiseases();
  const idx = list.findIndex((d) => d.id === disease.id);
  const clean = normaliseDisease(disease);
  if (idx >= 0) list[idx] = clean;
  else list.push(clean);
  writeCustomDiseases(list);
  return clean;
};

export const deleteCustomDisease = (id) => {
  const list = readCustomDiseases().filter((d) => d.id !== id);
  writeCustomDiseases(list);
};

/**
 * Slugify a disease name into a stable id. Falls back to a random suffix
 * if empty. Used when the user creates a brand-new disease from the UI.
 */
export const slugId = (name) => {
  const base = (name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (!base) return `custom-${Date.now().toString(36)}`;
  return base;
};

const normaliseDisease = (d) => ({
  id: d.id || slugId(d.name),
  name: (d.name || "").trim() || "Untitled",
  species: Array.isArray(d.species) && d.species.length ? d.species : ["canine"],
  category: (d.category || "Custom").trim(),
  clinicalSigns: (d.clinicalSigns || "").trim(),
  diagnostics: Array.isArray(d.diagnostics)
    ? d.diagnostics
        .filter((t) => t && (t.test || t.finding))
        .map((t) => ({ test: (t.test || "").trim(), finding: (t.finding || "").trim() }))
    : [],
  differentials: Array.isArray(d.differentials)
    ? d.differentials.map((s) => (s || "").trim()).filter(Boolean)
    : [],
  medications: Array.isArray(d.medications)
    ? d.medications
        .filter((m) => m && (m.drug || m.doseText))
        .map((m) => ({
          drug: (m.drug || "").trim(),
          doseMgPerKg: m.doseMgPerKg === "" || m.doseMgPerKg == null ? null : Number(m.doseMgPerKg),
          doseText: (m.doseText || "").trim(),
          route: (m.route || "PO").trim(),
          frequency: (m.frequency || "SID").trim(),
          duration: (m.duration || "").trim(),
          notes: (m.notes || "").trim(),
        }))
    : [],
  supportiveCare: Array.isArray(d.supportiveCare)
    ? d.supportiveCare.map((s) => (s || "").trim()).filter(Boolean)
    : [],
  __custom: true,
});

/** Empty disease scaffold — used by the "Add disease" UI. */
export const blankDisease = () => ({
  id: "",
  name: "",
  species: ["canine"],
  category: "Custom",
  clinicalSigns: "",
  diagnostics: [{ test: "", finding: "" }],
  differentials: [""],
  medications: [
    { drug: "", doseMgPerKg: null, doseText: "", route: "PO", frequency: "SID", duration: "", notes: "" },
  ],
  supportiveCare: [""],
});

/** Aggregated drug library across every disease (built-in + custom) + user-added drugs. */
export const getDrugLibrary = () => {
  const seen = new Map();
  // 1) Disease-derived drugs
  for (const d of getAllDiseases()) {
    for (const m of d.medications || []) {
      const key = (m.drug || "").toLowerCase().replace(/\s*\(.*?\)/g, "").trim();
      if (!key || seen.has(key)) continue;
      seen.set(key, {
        drug: m.drug.replace(/\s*\(.*?\)/g, "").trim(),
        doseMgPerKg: m.doseMgPerKg ?? null,
        doseText: m.doseText || "",
        route: m.route || "PO",
        frequency: m.frequency || "SID",
        duration: m.duration || "",
        notes: m.notes || "",
        __source: "disease",
      });
    }
  }
  // 2) User-added standalone drugs — override disease-derived on same key
  for (const m of readCustomDrugs()) {
    const key = (m.drug || "").toLowerCase().replace(/\s*\(.*?\)/g, "").trim();
    if (!key) continue;
    seen.set(key, { ...m, __source: "custom" });
  }
  return Array.from(seen.values()).sort((a, b) => a.drug.localeCompare(b.drug));
};

/* ----------------------- Custom drug store (Formulary) ----------------------- */

export const readCustomDrugs = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(CUSTOM_DRUGS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const writeCustomDrugs = (list) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(CUSTOM_DRUGS_KEY, JSON.stringify(list));
};

export const normaliseDrug = (m) => ({
  drug: (m.drug || "").trim(),
  doseMgPerKg:
    m.doseMgPerKg === "" || m.doseMgPerKg == null || Number.isNaN(Number(m.doseMgPerKg))
      ? null
      : Number(m.doseMgPerKg),
  doseText: (m.doseText || "").trim(),
  route: (m.route || "PO").trim(),
  frequency: (m.frequency || "SID").trim(),
  duration: (m.duration || "").trim(),
  notes: (m.notes || "").trim(),
});

export const saveCustomDrug = (m) => {
  const clean = normaliseDrug(m);
  if (!clean.drug) return null;
  const key = clean.drug.toLowerCase();
  const list = readCustomDrugs();
  const idx = list.findIndex((d) => d.drug.toLowerCase() === key);
  if (idx >= 0) list[idx] = clean;
  else list.push(clean);
  writeCustomDrugs(list);
  return clean;
};

export const deleteCustomDrug = (drugName) => {
  const key = (drugName || "").toLowerCase().trim();
  writeCustomDrugs(readCustomDrugs().filter((d) => d.drug.toLowerCase() !== key));
};

/** Bulk import — returns { added, updated, skipped }. */
export const importCustomDrugs = (rows) => {
  let added = 0;
  let updated = 0;
  let skipped = 0;
  const existing = readCustomDrugs();
  const map = new Map(existing.map((d) => [d.drug.toLowerCase(), d]));
  for (const r of rows) {
    const drug = (r.drug || r.Drug || r.name || r.Name || "").toString().trim();
    if (!drug) {
      skipped++;
      continue;
    }
    const mgkgRaw = r.dose_mg_per_kg ?? r.doseMgPerKg ?? r.mg_per_kg ?? r["mg/kg"] ?? r.mgkg ?? null;
    const doseText =
      r.dose_label ?? r.doseText ?? r.dose ?? r.Dose ?? (mgkgRaw != null && mgkgRaw !== "" ? `${mgkgRaw} mg/kg` : "");
    const clean = normaliseDrug({
      drug,
      doseMgPerKg: mgkgRaw,
      doseText,
      route: r.route ?? r.Route ?? "PO",
      frequency: r.frequency ?? r.freq ?? r.Frequency ?? "SID",
      duration: r.duration ?? r.Duration ?? "",
      notes: r.notes ?? r.Notes ?? "",
    });
    const key = clean.drug.toLowerCase();
    if (map.has(key)) updated++;
    else added++;
    map.set(key, clean);
  }
  writeCustomDrugs(Array.from(map.values()));
  return { added, updated, skipped };
};

/** Convenience for existing consumers that referenced DRUG_LIBRARY directly. */
export const DRUG_LIBRARY = getDrugLibrary();

/** Blank medication row used by the Rx editor and disease editor. */
export const blankMedication = () => ({
  drug: "",
  doseMgPerKg: null,
  doseText: "",
  route: "PO",
  frequency: "SID",
  duration: "",
  notes: "",
});

/** mg/kg × body-weight → formatted total dose. */
export const calcDose = (doseMgPerKg, weightKg) => {
  if (doseMgPerKg == null || !weightKg) return null;
  const total = doseMgPerKg * weightKg;
  const rounded = total < 1 ? total.toFixed(2) : total < 10 ? total.toFixed(1) : Math.round(total);
  return `${rounded} mg`;
};
